import { ClientSideAccessiblePublicEnvValues } from "@/common/domain/constants/env/ClientSideAccessiblePublicEnvValues";
import { PageApiWithShareableUrl } from "@/common/domain/entities/routing/PageApi";
import { constructRoute } from "@/common/application/routing/constructRoute";

/**
 * Generates the path to the route that will be used to render the results of a
 * search.
 * @param resource defines what kind of resource the user is looking for. You
 *   can find all available resources in [Wezoo] static class.
 * @param location defines the geographic location near which the user is
 *   looking for a resource.
 * @param filters define which filters the user has applied.
 * @param properties defines any key-value-pair filters or other properties
 *   that the user has applied. These are ignored in SEO.
 * @return relative path to the route from the web app root.
 */
export interface DiscoverRouteGetPathProps {
    uid?: string;
    params?: Record<string, string | number | undefined>;
    host?: string;
    location?: string;
    type?: "ROOM" | "HOTDESK" | "DAYOFFICE" | "PRIVATE_OFFICE" | "VIRTUAL_OFFICE";
    coordinates?: {
        latitude: number;
        longitude: number;
        zoom?: number;
    };
    latitude?: number;
    longitude?: number;
    zoom?: number;
    // Optional [west, south, east, north] (lon/lat, EPSG:4326). When set,
    // the discover map fits these bounds on load instead of geocoding
    // `location`. Both can be passed simultaneously -- `location` then
    // acts as a human-readable fallback if `bbox` fails to parse.
    bbox?: [number, number, number, number];
}

/**
 * Used to generate paths to the resource search route on-demand. Use the
 * [getRoute] to create a path to the route with (optionally) parameters that
 * will be used in the path.
 */
export const DiscoverRouteApi: PageApiWithShareableUrl<
    DiscoverRouteGetPathProps,
    DiscoverRouteGetPathProps
> = {
    getRoute: ({
        host,
        type,
        location,
        coordinates,
        bbox,
        params = {},
        uid,
    }) => {
        if (uid) {
            return constructRoute(["discover", uid], params);
        }
        if (host) {
            params["host"] = host;
        }
        if (location) {
            params["location"] = location;
        }
        if (type) {
            params["type"] = type;
        }
        if (coordinates) {
            params["latitude"] = coordinates.latitude.toString();
            params["longitude"] = coordinates.longitude.toString();
            if (coordinates.zoom) {
                params["zoom"] = coordinates.zoom.toString();
            }
        }
        if (bbox) {
            // "w,s,e,n", four floats. Rounded to 4 decimals (~11m at the
            // equator) so the URL stays short without sacrificing fit
            // accuracy at any zoom level the map allows.
            params["bbox"] = bbox.map((n) => n.toFixed(4)).join(",");
        }
        return constructRoute(["discover"], params);
    },

    getShareableUrl: (props) => {
        return `${ClientSideAccessiblePublicEnvValues.wezooWebAppUrl}${DiscoverRouteApi.getRoute(
            props
        )}`;
    },
};
